/**
 * BrandingPanel - Elegant company branding/design guide configuration
 *
 * Design Principles Applied:
 * - Progressive disclosure: Start minimal, expand for details
 * - Dropdowns for presets: Cleaner than horizontal scroll
 * - Inline previews: Visual feedback without extra space
 * - Consistent shadcn patterns: Matches framework styling
 */

import React, { useState, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Palette,
  ChevronDown,
  Type,
  Image as ImageIcon,
  Upload,
  X,
  Settings2,
  Sparkles,
  Sun,
  Moon,
} from 'lucide-react';
import type { BrandingConfig } from '@/types/outline';

// ============================================================================
// Color Theme Presets - Pantone Colors of the Year, Corporate & Creative Palettes
// Each theme has light & dark variants (same brand colors, different text/background)
// ============================================================================
type ThemeMode = 'light' | 'dark';

type ColorTheme = {
  id: string;
  name: string;
  subtitle: string;
  category: 'Pantone Classics' | 'Corporate & Finance' | 'Tech & Innovation' | 'Creative & Bold' | 'Nature & Sustainability' | 'Luxury & Premium' | 'Minimalist';
  primary: string;
  secondary: string;
  accent: string;
  light: { text: string; background: string };
  dark: { text: string; background: string };
};

const colorThemes: ColorTheme[] = [
  // ── Pantone Colors of the Year ───────────────────────────────────
  { id: 'cloud-dancer', name: 'Cloud Dancer', subtitle: 'Pantone 2026', category: 'Pantone Classics',
    primary: '#4A6FA5', secondary: '#7B8FA8', accent: '#E8B4B8',
    light: { text: '#2D3748', background: '#F0EEE9' }, dark: { text: '#dbeafe', background: '#172554' } },
  { id: 'mocha-mousse', name: 'Mocha Mousse', subtitle: 'Pantone 2025', category: 'Pantone Classics',
    primary: '#A47764', secondary: '#924622', accent: '#E6C2B2',
    light: { text: '#35170C', background: '#F7E8E1' }, dark: { text: '#ffedd5', background: '#431407' } },
  { id: 'peach-fuzz', name: 'Peach Fuzz', subtitle: 'Pantone 2024', category: 'Pantone Classics',
    primary: '#FFBE98', secondary: '#E07A5F', accent: '#3D405B',
    light: { text: '#2D2D2D', background: '#fff7ed' }, dark: { text: '#ffedd5', background: '#431407' } },
  { id: 'viva-magenta', name: 'Viva Magenta', subtitle: 'Pantone 2023', category: 'Pantone Classics',
    primary: '#BB2649', secondary: '#6B4C4C', accent: '#D4A574',
    light: { text: '#1A1A1A', background: '#fff1f2' }, dark: { text: '#ffe4e6', background: '#4c0519' } },
  { id: 'very-peri', name: 'Very Peri', subtitle: 'Pantone 2022', category: 'Pantone Classics',
    primary: '#6667AB', secondary: '#3D3B8E', accent: '#F0B4B4',
    light: { text: '#1E1B4B', background: '#eef2ff' }, dark: { text: '#e0e7ff', background: '#1e1b4b' } },
  { id: 'classic-blue', name: 'Classic Blue', subtitle: 'Pantone 2020', category: 'Pantone Classics',
    primary: '#0F4C81', secondary: '#1B6CA8', accent: '#F0C75E',
    light: { text: '#0A1929', background: '#f0f9ff' }, dark: { text: '#e0f2fe', background: '#0c4a6e' } },

  // ── Corporate & Finance ──────────────────────────────────────────
  { id: 'corporate-trust', name: 'Corporate Trust', subtitle: 'Professional', category: 'Corporate & Finance',
    primary: '#3D5A80', secondary: '#98C1D9', accent: '#EE6C4D',
    light: { text: '#293241', background: '#ecfeff' }, dark: { text: '#e2e8f0', background: '#1e293b' } },
  { id: 'oxford-navy', name: 'Oxford Navy', subtitle: 'Authority', category: 'Corporate & Finance',
    primary: '#14213D', secondary: '#003459', accent: '#FCA311',
    light: { text: '#0D1117', background: '#f1f5f9' }, dark: { text: '#e2e8f0', background: '#0f172a' } },
  { id: 'boardroom', name: 'Boardroom', subtitle: 'Executive', category: 'Corporate & Finance',
    primary: '#003459', secondary: '#007EA7', accent: '#00A8E8',
    light: { text: '#00171F', background: '#f0f9ff' }, dark: { text: '#e0f2fe', background: '#082f49' } },
  { id: 'slate-executive', name: 'Slate Executive', subtitle: 'Enterprise', category: 'Corporate & Finance',
    primary: '#475569', secondary: '#334155', accent: '#3B82F6',
    light: { text: '#0F172A', background: '#f8fafc' }, dark: { text: '#e2e8f0', background: '#1e293b' } },

  // ── Tech & Innovation ────────────────────────────────────────────
  { id: 'ethereal-teal', name: 'Ethereal Teal', subtitle: 'Tech & Wellness', category: 'Tech & Innovation',
    primary: '#177E89', secondary: '#084C61', accent: '#FFC857',
    light: { text: '#323031', background: '#f0fdfa' }, dark: { text: '#ccfbf1', background: '#134e4a' } },
  { id: 'digital-indigo', name: 'Digital Indigo', subtitle: 'SaaS', category: 'Tech & Innovation',
    primary: '#4F46E5', secondary: '#3730A3', accent: '#06B6D4',
    light: { text: '#1E1B4B', background: '#eef2ff' }, dark: { text: '#e0e7ff', background: '#312e81' } },
  { id: 'neon-horizon', name: 'Neon Horizon', subtitle: 'Futuristic', category: 'Tech & Innovation',
    primary: '#7C3AED', secondary: '#4C1D95', accent: '#F59E0B',
    light: { text: '#1C1917', background: '#f5f3ff' }, dark: { text: '#ede9fe', background: '#4c1d95' } },
  { id: 'arctic-interface', name: 'Arctic Interface', subtitle: 'Clean Tech', category: 'Tech & Innovation',
    primary: '#0EA5E9', secondary: '#0369A1', accent: '#F97316',
    light: { text: '#0C4A6E', background: '#F0F9FF' }, dark: { text: '#e0f2fe', background: '#0c4a6e' } },

  // ── Creative & Bold ──────────────────────────────────────────────
  { id: 'coral-reef', name: 'Coral Reef', subtitle: 'Vibrant', category: 'Creative & Bold',
    primary: '#F97066', secondary: '#DC2626', accent: '#0D9488',
    light: { text: '#1C1917', background: '#FFF7ED' }, dark: { text: '#fee2e2', background: '#450a0a' } },
  { id: 'electric-sunset', name: 'Electric Sunset', subtitle: 'Energetic', category: 'Creative & Bold',
    primary: '#EA580C', secondary: '#9A3412', accent: '#7C3AED',
    light: { text: '#1C1917', background: '#FFFBEB' }, dark: { text: '#fef3c7', background: '#431407' } },
  { id: 'studio-pop', name: 'Studio Pop', subtitle: 'Creative', category: 'Creative & Bold',
    primary: '#EC4899', secondary: '#BE185D', accent: '#14B8A6',
    light: { text: '#1E1E1E', background: '#FDF2F8' }, dark: { text: '#fce7f3', background: '#831843' } },

  // ── Nature & Sustainability ──────────────────────────────────────
  { id: 'nature-earth', name: 'Nature Earth', subtitle: 'Sustainable', category: 'Nature & Sustainability',
    primary: '#606C38', secondary: '#283618', accent: '#DDA15E',
    light: { text: '#1E1E1E', background: '#f7fee7' }, dark: { text: '#ecfccb', background: '#365314' } },
  { id: 'forest-canopy', name: 'Forest Canopy', subtitle: 'Organic', category: 'Nature & Sustainability',
    primary: '#166534', secondary: '#14532D', accent: '#CA8A04',
    light: { text: '#052E16', background: '#F0FDF4' }, dark: { text: '#dcfce7', background: '#14532d' } },
  { id: 'desert-sage', name: 'Desert Sage', subtitle: 'Earthy', category: 'Nature & Sustainability',
    primary: '#A3B18A', secondary: '#588157', accent: '#BC6C25',
    light: { text: '#344E41', background: '#f7fee7' }, dark: { text: '#ecfccb', background: '#365314' } },
  { id: 'ocean-drift', name: 'Ocean Drift', subtitle: 'Coastal', category: 'Nature & Sustainability',
    primary: '#155E75', secondary: '#164E63', accent: '#FB923C',
    light: { text: '#0C4A6E', background: '#ECFEFF' }, dark: { text: '#cffafe', background: '#164e63' } },

  // ── Luxury & Premium ─────────────────────────────────────────────
  { id: 'midnight-luxe', name: 'Midnight Luxe', subtitle: 'Premium Dark', category: 'Luxury & Premium',
    primary: '#C9A227', secondary: '#8B7355', accent: '#D4AF37',
    light: { text: '#1A1A1A', background: '#fffbeb' }, dark: { text: '#fef3c7', background: '#422006' } },
  { id: 'royal-obsidian', name: 'Royal Obsidian', subtitle: 'Opulent', category: 'Luxury & Premium',
    primary: '#A78BFA', secondary: '#7C3AED', accent: '#C0C0C0',
    light: { text: '#1A1A2E', background: '#f5f3ff' }, dark: { text: '#ede9fe', background: '#2e1065' } },
  { id: 'burgundy-velvet', name: 'Burgundy Velvet', subtitle: 'Prestige', category: 'Luxury & Premium',
    primary: '#881337', secondary: '#4C0519', accent: '#D4A574',
    light: { text: '#1A0A0F', background: '#FFF1F2' }, dark: { text: '#ffe4e6', background: '#4c0519' } },

  // ── Minimalist ───────────────────────────────────────────────────
  { id: 'sophisticate', name: 'Sophisticate', subtitle: 'Elegant', category: 'Minimalist',
    primary: '#474973', secondary: '#A69CAC', accent: '#F1DAC4',
    light: { text: '#161B33', background: '#f8fafc' }, dark: { text: '#e2e8f0', background: '#1e1b4b' } },
  { id: 'deep-ocean', name: 'Deep Ocean', subtitle: 'Refined', category: 'Minimalist',
    primary: '#033F63', secondary: '#28666E', accent: '#B5B682',
    light: { text: '#1A1A2E', background: '#FEDC97' }, dark: { text: '#e0f2fe', background: '#082f49' } },
  { id: 'swiss-minimal', name: 'Swiss Minimal', subtitle: 'Bauhaus', category: 'Minimalist',
    primary: '#18181B', secondary: '#3F3F46', accent: '#DC2626',
    light: { text: '#09090B', background: '#f4f4f5' }, dark: { text: '#e4e4e7', background: '#18181b' } },
  { id: 'warm-neutral', name: 'Warm Neutral', subtitle: 'Understated', category: 'Minimalist',
    primary: '#78716C', secondary: '#44403C', accent: '#D97706',
    light: { text: '#1C1917', background: '#f5f5f4' }, dark: { text: '#e7e5e4', background: '#292524' } },
];

// Group themes by category for the dropdown
const themeCategories = Array.from(new Set(colorThemes.map(t => t.category)));
const themesByCategory = themeCategories.map(category => ({
  category,
  themes: colorThemes.filter(t => t.category === category),
}));

// Detect if a background color is dark (relative luminance < 0.5)
function isDarkBackground(hex: string): boolean {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance < 0.4;
}

// Resolve a theme's text/background for a given mode
function resolveThemeColors(theme: ColorTheme, mode: ThemeMode) {
  const variant = mode === 'dark' ? theme.dark : theme.light;
  return { text: variant.text, background: variant.background };
}

// ============================================================================
// Font Pairings - Based on Typography Design Principles
// ============================================================================
const fontPairings = [
  { id: 'modern-clean', name: 'Modern Clean', heading: 'Inter', body: 'Inter', category: 'Modern' },
  { id: 'tech-forward', name: 'Tech Forward', heading: 'Space Grotesk', body: 'Inter', category: 'Modern' },
  { id: 'startup', name: 'Startup', heading: 'Plus Jakarta Sans', body: 'Inter', category: 'Modern' },
  { id: 'editorial', name: 'Editorial', heading: 'Playfair Display', body: 'Source Sans Pro', category: 'Classic' },
  { id: 'sophisticated', name: 'Sophisticated', heading: 'DM Serif Display', body: 'DM Sans', category: 'Classic' },
  { id: 'traditional', name: 'Traditional', heading: 'Crimson Pro', body: 'Work Sans', category: 'Classic' },
  { id: 'corporate', name: 'Corporate', heading: 'Montserrat', body: 'Open Sans', category: 'Professional' },
  { id: 'enterprise', name: 'Enterprise', heading: 'Montserrat', body: 'Source Sans Pro', category: 'Professional' },
  { id: 'friendly', name: 'Friendly', heading: 'Poppins', body: 'Nunito', category: 'Approachable' },
  { id: 'documentation', name: 'Documentation', heading: 'Work Sans', body: 'Inter', category: 'Technical' },
];

const allFonts = [
  'Inter', 'Roboto', 'Poppins', 'Open Sans', 'Montserrat', 'Lato',
  'Playfair Display', 'Source Sans Pro', 'Source Serif Pro', 'Raleway',
  'Nunito', 'Work Sans', 'DM Sans', 'DM Serif Display', 'Space Grotesk',
  'Plus Jakarta Sans', 'Merriweather', 'Crimson Pro',
];

// ============================================================================
// Component
// ============================================================================

interface BrandingPanelProps {
  conversationId: string;
  branding: BrandingConfig | undefined;
  onBrandingChange: (branding: BrandingConfig) => void;
  onBrandingClear: () => Promise<void>;
  onLogoUpload: (file: File) => Promise<void>;
  onLogoDelete: () => Promise<void>;
  disabled?: boolean;
}

function isValidHexColor(color: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(color);
}

// Color swatch with optional click-to-edit
const ColorSwatch: React.FC<{
  color: string;
  size?: 'sm' | 'md';
  className?: string;
  onClick?: () => void;
}> = ({ color, size = 'sm', className, onClick }) => (
  <div
    className={cn(
      "rounded-sm border border-black/10 dark:border-white/20",
      size === 'sm' ? 'w-4 h-4' : 'w-6 h-6',
      onClick && 'cursor-pointer hover:ring-2 hover:ring-primary/50',
      className
    )}
    style={{ backgroundColor: color }}
    onClick={onClick}
  />
);

// Inline color editor in popover
const ColorEditor: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ label, value, onChange, disabled }) => {
  const [localValue, setLocalValue] = useState(value);

  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={localValue || '#000000'}
        onChange={(e) => {
          setLocalValue(e.target.value);
          onChange(e.target.value);
        }}
        disabled={disabled}
        className="w-8 h-8 rounded cursor-pointer border-0 p-0"
      />
      <div className="flex-1">
        <Label className="text-xs text-muted-foreground">{label}</Label>
        <Input
          value={localValue}
          onChange={(e) => {
            const val = e.target.value.startsWith('#') ? e.target.value : `#${e.target.value}`;
            setLocalValue(val);
            if (isValidHexColor(val)) onChange(val);
          }}
          disabled={disabled}
          placeholder="#000000"
          className={cn(
            "h-7 text-xs font-mono",
            localValue && !isValidHexColor(localValue) && "border-destructive"
          )}
        />
      </div>
    </div>
  );
};

export const BrandingPanel: React.FC<BrandingPanelProps> = ({
  branding,
  onBrandingChange,
  onBrandingClear,
  onLogoUpload,
  onLogoDelete,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(!!branding);
  const [isUploading, setIsUploading] = useState(false);
  const [logoHtml, setLogoHtml] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Detect current mode from branding background color
  const [themeMode, setThemeMode] = useState<ThemeMode>(
    () => branding?.colors?.background && isDarkBackground(branding.colors.background) ? 'dark' : 'light'
  );

  // Derive current theme ID from colors (match on primary + secondary, mode-independent)
  const currentThemeId = colorThemes.find(
    t => t.primary === branding?.colors?.primary && t.secondary === branding?.colors?.secondary
  )?.id || '';

  // Derive current font pairing ID
  const currentFontPairingId = fontPairings.find(
    p => p.heading === branding?.fonts?.headline && p.body === branding?.fonts?.body
  )?.id || '';

  const applyThemeWithMode = (theme: ColorTheme, mode: ThemeMode) => {
    const { text, background } = resolveThemeColors(theme, mode);
    onBrandingChange({
      ...branding,
      colors: {
        primary: theme.primary,
        secondary: theme.secondary,
        accent: theme.accent,
        textPrimary: text,
        background: background,
      },
      fonts: branding?.fonts,
      logo: branding?.logo,
    });
  };

  const handleThemeSelect = (themeId: string) => {
    const theme = colorThemes.find(t => t.id === themeId);
    if (!theme) return;
    applyThemeWithMode(theme, themeMode);
  };

  const handleModeToggle = () => {
    const newMode: ThemeMode = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(newMode);
    // If a theme is currently active, re-apply it in the new mode
    if (currentThemeId) {
      const theme = colorThemes.find(t => t.id === currentThemeId);
      if (theme) applyThemeWithMode(theme, newMode);
    }
  };

  const handleFontPairingSelect = (pairingId: string) => {
    const pairing = fontPairings.find(p => p.id === pairingId);
    if (!pairing) return;

    onBrandingChange({
      ...branding,
      colors: branding?.colors || { primary: '#3D5A80', secondary: '#98C1D9' },
      fonts: { headline: pairing.heading, body: pairing.body },
      logo: branding?.logo,
    });
  };

  const handleCustomFontChange = (type: 'headline' | 'body', value: string) => {
    onBrandingChange({
      ...branding,
      colors: branding?.colors || { primary: '#3D5A80', secondary: '#98C1D9' },
      fonts: {
        headline: type === 'headline' ? value : (branding?.fonts?.headline || ''),
        body: type === 'body' ? value : (branding?.fonts?.body || ''),
      },
      logo: branding?.logo,
    });
  };

  const handleColorChange = (key: string, value: string) => {
    if (!isValidHexColor(value)) return;
    onBrandingChange({
      ...branding,
      colors: {
        primary: branding?.colors?.primary || '#3D5A80',
        secondary: branding?.colors?.secondary || '#98C1D9',
        ...branding?.colors,
        [key]: value,
      },
      fonts: branding?.fonts,
      logo: branding?.logo,
    });
  };

  const handleFileSelect = async (file: File) => {
    if (!file) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a PNG, JPEG, or SVG file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('File too large. Maximum size is 10MB.');
      return;
    }
    setIsUploading(true);
    try {
      await onLogoUpload(file);
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogoHtmlSave = () => {
    if (!logoHtml.trim()) return;
    const encodedHtml = btoa(logoHtml);
    onBrandingChange({
      ...branding,
      colors: branding?.colors || { primary: '#3D5A80', secondary: '#98C1D9' },
      logo: {
        url: `data:text/html;base64,${encodedHtml}`,
        gcsPath: '',
        position: branding?.logo?.position || 'top-right',
        maxHeight: branding?.logo?.maxHeight || 60,
      },
    });
    setLogoHtml('');
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          className={cn(
            "w-full justify-between h-9 mb-2",
            "text-muted-foreground hover:text-foreground",
            "border border-border hover:border-border/80",
            "hover:bg-accent/50",
            isOpen && "bg-accent/30 border-border/80"
          )}
        >
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">Brand Guide</span>
            {branding?.colors && (
              <div className="flex items-center gap-0.5 ml-2">
                <ColorSwatch color={branding.colors.primary} />
                <ColorSwatch color={branding.colors.secondary} />
                {branding.colors.accent && <ColorSwatch color={branding.colors.accent} />}
                {branding.colors.textPrimary && <ColorSwatch color={branding.colors.textPrimary} />}
                {branding.colors.background && <ColorSwatch color={branding.colors.background} />}
              </div>
            )}
          </div>
          <ChevronDown className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")} />
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="rounded-lg border bg-card/50 p-3 mb-3 space-y-3">
          {/* Color Theme Row */}
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
            <div className="flex items-center gap-2 text-muted-foreground md:w-20 flex-shrink-0">
              <Palette className="w-4 h-4" />
              <span className="text-xs font-medium">Theme</span>
            </div>
            <div className="flex items-center gap-2 md:gap-3 w-full md:flex-1 min-w-0">
            <Select
              value={currentThemeId || (branding ? '_custom' : '_none')}
              onValueChange={(v) => {
                if (v === '_reset') {
                  onBrandingClear();
                } else if (v !== '_custom' && v !== '_none') {
                  handleThemeSelect(v);
                }
              }}
              disabled={disabled}
            >
              <SelectTrigger className="h-9 flex-1 min-w-0">
                {currentThemeId ? (
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                      {(() => {
                        const theme = colorThemes.find(t => t.id === currentThemeId);
                        if (!theme) return null;
                        const resolved = resolveThemeColors(theme, themeMode);
                        return (
                          <>
                            <ColorSwatch color={theme.primary} />
                            <ColorSwatch color={theme.secondary} />
                            <ColorSwatch color={theme.accent} />
                            <ColorSwatch color={resolved.text} />
                            <ColorSwatch color={resolved.background} />
                          </>
                        );
                      })()}
                    </div>
                    <span className="text-sm">{colorThemes.find(t => t.id === currentThemeId)?.name}</span>
                  </div>
                ) : branding?.colors ? (
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                      <ColorSwatch color={branding.colors.primary} />
                      <ColorSwatch color={branding.colors.secondary} />
                      {branding.colors.accent && <ColorSwatch color={branding.colors.accent} />}
                      {branding.colors.textPrimary && <ColorSwatch color={branding.colors.textPrimary} />}
                      {branding.colors.background && <ColorSwatch color={branding.colors.background} />}
                    </div>
                    <span className="text-sm text-muted-foreground">Custom</span>
                  </div>
                ) : (
                  <span className="text-muted-foreground">Select color theme...</span>
                )}
              </SelectTrigger>
              <SelectContent>
                {/* Reset option - only shown if branding exists */}
                {branding && (
                  <>
                    <SelectItem value="_reset">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <X className="w-3 h-3" />
                        <span>Use Template Defaults</span>
                      </div>
                    </SelectItem>
                    <Separator className="my-1" />
                  </>
                )}
                {themesByCategory.map(({ category, themes }) => (
                  <SelectGroup key={category}>
                    <SelectLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1.5">
                      {category}
                    </SelectLabel>
                    {themes.map((theme) => {
                      const resolved = resolveThemeColors(theme, themeMode);
                      return (
                        <SelectItem key={theme.id} value={theme.id}>
                          <div className="flex items-center gap-3">
                            <div className="flex gap-0.5">
                              <ColorSwatch color={theme.primary} />
                              <ColorSwatch color={theme.secondary} />
                              <ColorSwatch color={theme.accent} />
                              <ColorSwatch color={resolved.text} />
                              <ColorSwatch color={resolved.background} />
                            </div>
                            <div>
                              <div className="text-sm font-medium">{theme.name}</div>
                              <div className="text-xs text-muted-foreground">{theme.subtitle}</div>
                            </div>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            {/* Light/Dark mode toggle — segmented pill */}
            <div className="flex h-9 flex-shrink-0 rounded-md border border-input bg-muted/30 p-0.5">
              <button
                type="button"
                disabled={disabled}
                onClick={() => { if (themeMode !== 'light') handleModeToggle(); }}
                className={cn(
                  "flex items-center gap-1 rounded-sm px-2 text-xs font-medium transition-all",
                  themeMode === 'light'
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Sun className="w-3 h-3" />
                Light
              </button>
              <button
                type="button"
                disabled={disabled}
                onClick={() => { if (themeMode !== 'dark') handleModeToggle(); }}
                className={cn(
                  "flex items-center gap-1 rounded-sm px-2 text-xs font-medium transition-all",
                  themeMode === 'dark'
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Moon className="w-3 h-3" />
                Dark
              </button>
            </div>
            {/* Advanced color customization */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0" disabled={disabled}>
                  <Settings2 className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72" align="end">
                <div className="space-y-3">
                  <div className="font-medium text-sm">Custom Colors</div>
                  <div className="grid gap-3">
                    <ColorEditor
                      label="Primary"
                      value={branding?.colors?.primary || '#3D5A80'}
                      onChange={(v) => handleColorChange('primary', v)}
                      disabled={disabled}
                    />
                    <ColorEditor
                      label="Secondary"
                      value={branding?.colors?.secondary || '#98C1D9'}
                      onChange={(v) => handleColorChange('secondary', v)}
                      disabled={disabled}
                    />
                    <ColorEditor
                      label="Accent"
                      value={branding?.colors?.accent || ''}
                      onChange={(v) => handleColorChange('accent', v)}
                      disabled={disabled}
                    />
                    <Separator />
                    <ColorEditor
                      label="Text"
                      value={branding?.colors?.textPrimary || ''}
                      onChange={(v) => handleColorChange('textPrimary', v)}
                      disabled={disabled}
                    />
                    <ColorEditor
                      label="Background"
                      value={branding?.colors?.background || ''}
                      onChange={(v) => handleColorChange('background', v)}
                      disabled={disabled}
                    />
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            </div>
          </div>

          {/* Typography Row */}
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
            <div className="flex items-center gap-2 text-muted-foreground md:w-20 flex-shrink-0">
              <Type className="w-4 h-4" />
              <span className="text-xs font-medium">Fonts</span>
            </div>
            <div className="flex items-center gap-2 md:gap-3 w-full md:flex-1 min-w-0">
            <Select value={currentFontPairingId || '_custom'} onValueChange={(v) => v !== '_custom' && handleFontPairingSelect(v)} disabled={disabled}>
              <SelectTrigger className="h-9 flex-1 min-w-0">
                {currentFontPairingId ? (
                  <span className="text-sm">
                    <span style={{ fontFamily: fontPairings.find(p => p.id === currentFontPairingId)?.heading }}>
                      {fontPairings.find(p => p.id === currentFontPairingId)?.name}
                    </span>
                    <span className="text-muted-foreground ml-1.5 text-xs">
                      ({branding?.fonts?.headline} / {branding?.fonts?.body})
                    </span>
                  </span>
                ) : branding?.fonts?.headline ? (
                  <span className="text-sm">
                    <span style={{ fontFamily: branding.fonts.headline }}>{branding.fonts.headline}</span>
                    <span className="text-muted-foreground"> / </span>
                    <span style={{ fontFamily: branding.fonts.body }}>{branding.fonts.body}</span>
                    <span className="text-muted-foreground ml-1.5 text-xs">(Custom)</span>
                  </span>
                ) : (
                  <span className="text-muted-foreground">Select font pairing...</span>
                )}
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Modern</SelectLabel>
                  {fontPairings.filter(p => p.category === 'Modern').map((pairing) => (
                    <SelectItem key={pairing.id} value={pairing.id}>
                      <div>
                        <span className="font-medium" style={{ fontFamily: pairing.heading }}>{pairing.name}</span>
                        <span className="text-xs text-muted-foreground ml-2" style={{ fontFamily: pairing.body }}>
                          {pairing.heading} / {pairing.body}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Classic</SelectLabel>
                  {fontPairings.filter(p => p.category === 'Classic').map((pairing) => (
                    <SelectItem key={pairing.id} value={pairing.id}>
                      <div>
                        <span className="font-medium" style={{ fontFamily: pairing.heading }}>{pairing.name}</span>
                        <span className="text-xs text-muted-foreground ml-2" style={{ fontFamily: pairing.body }}>
                          {pairing.heading} / {pairing.body}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Professional</SelectLabel>
                  {fontPairings.filter(p => p.category === 'Professional').map((pairing) => (
                    <SelectItem key={pairing.id} value={pairing.id}>
                      <div>
                        <span className="font-medium" style={{ fontFamily: pairing.heading }}>{pairing.name}</span>
                        <span className="text-xs text-muted-foreground ml-2" style={{ fontFamily: pairing.body }}>
                          {pairing.heading} / {pairing.body}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Other</SelectLabel>
                  {fontPairings.filter(p => !['Modern', 'Classic', 'Professional'].includes(p.category)).map((pairing) => (
                    <SelectItem key={pairing.id} value={pairing.id}>
                      <div>
                        <span className="font-medium" style={{ fontFamily: pairing.heading }}>{pairing.name}</span>
                        <span className="text-xs text-muted-foreground ml-2" style={{ fontFamily: pairing.body }}>
                          {pairing.heading} / {pairing.body}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {/* Custom font selection */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0" disabled={disabled}>
                  <Settings2 className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64" align="end">
                <div className="space-y-3">
                  <div className="font-medium text-sm">Custom Fonts</div>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Heading</Label>
                      <Select
                        value={branding?.fonts?.headline || ''}
                        onValueChange={(v) => handleCustomFontChange('headline', v)}
                        disabled={disabled}
                      >
                        <SelectTrigger className="h-8 mt-1">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {allFonts.map((font) => (
                            <SelectItem key={font} value={font}>
                              <span style={{ fontFamily: font }}>{font}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Body</Label>
                      <Select
                        value={branding?.fonts?.body || ''}
                        onValueChange={(v) => handleCustomFontChange('body', v)}
                        disabled={disabled}
                      >
                        <SelectTrigger className="h-8 mt-1">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {allFonts.map((font) => (
                            <SelectItem key={font} value={font}>
                              <span style={{ fontFamily: font }}>{font}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            </div>
          </div>

          {/* Logo Row */}
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:gap-3">
            <div className="flex items-center gap-2 text-muted-foreground md:w-20 flex-shrink-0 md:pt-2">
              <ImageIcon className="w-4 h-4" />
              <span className="text-xs font-medium">Logo</span>
            </div>
            <div className="flex-1">
              {branding?.logo ? (
                <div className="flex items-center gap-2 p-2 rounded-md border bg-muted/30">
                  {branding.logo.url.startsWith('data:text/html') ? (
                    <div className="w-10 h-7 flex items-center justify-center bg-muted rounded text-[10px] text-muted-foreground">
                      HTML
                    </div>
                  ) : (
                    <img src={branding.logo.url} alt="Logo" className="h-7 max-w-[60px] object-contain" />
                  )}
                  <Select
                    value={branding.logo.position || 'top-right'}
                    onValueChange={(v) => onBrandingChange({
                      ...branding,
                      logo: { ...branding.logo!, position: v as any },
                    })}
                    disabled={disabled}
                  >
                    <SelectTrigger className="h-7 text-xs flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="top-left">Top Left</SelectItem>
                      <SelectItem value="top-right">Top Right</SelectItem>
                      <SelectItem value="bottom-left">Bottom Left</SelectItem>
                      <SelectItem value="bottom-right">Bottom Right</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={onLogoDelete}
                    disabled={disabled || isUploading}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 flex-1 text-xs"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || isUploading}
                  >
                    <Upload className="w-3.5 h-3.5 mr-1.5" />
                    {isUploading ? 'Uploading...' : 'Upload Image'}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                    disabled={disabled || isUploading}
                    className="hidden"
                  />
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="h-9 text-xs" disabled={disabled}>
                        HTML/SVG
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72" align="end">
                      <div className="space-y-2">
                        <Label className="text-xs">Paste SVG or HTML code</Label>
                        <Textarea
                          value={logoHtml}
                          onChange={(e) => setLogoHtml(e.target.value)}
                          placeholder='<svg width="100" height="40">...</svg>'
                          className="h-20 text-xs font-mono resize-none"
                        />
                        <Button
                          size="sm"
                          className="w-full h-8"
                          onClick={handleLogoHtmlSave}
                          disabled={!logoHtml.trim()}
                        >
                          Apply
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
